import "../../assets/styles/Elements.scss";

import { Button, CircularProgress, Grid, MenuItem, Select } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { ApiErrors } from 'common/backend/api-errors/errors';
import { Investment } from 'common/backend/generated/investment-model';
import { InvestmentStatusTransition, investmentStatusTransitions } from "common/backend/types/investment-status-flow";
import { InvestmentFilter, InvestmentSelector, InvestmentStatusValues, RoleNames } from 'common/backend/types/utils';
import { CommonErrorDialog } from 'common/components/CommonErrorDialog';
import { GetInvestments, UpdateInvestment } from 'common/graphql/queryDescriptors';
import i18n from "common/services/i18n";
import { useCustomQuery } from 'common/store/hooks/useCustomQuery';
import { GetInvestmentManageable, InvestmentManageable, UpdateInvestmentManageable } from 'common/store/investment/types';
import { toUpperCaseFirst } from 'common/utils/utility';
import Navbar from 'components/Navbar';
import React from 'react';

interface Transition {
  status: InvestmentStatusValues;
  actionText: string;
}

const getStatusTransitionActionText = (status: InvestmentStatusValues) => {
  switch (status) {
    case InvestmentStatusValues.CANCELED:
      return i18n.t("Cancel");
    case InvestmentStatusValues.CONFIRMED:
      return i18n.t("Confirm");
    case InvestmentStatusValues.REVOKED:
      return i18n.t("Revoke");
    case InvestmentStatusValues.REFUNDED:
      return i18n.t("Refund");
    default:
      return "";
  }
}

const getStatusTransitions = (investment: InvestmentManageable) : Array<Transition> => {
  return investmentStatusTransitions
    .filter((transition: InvestmentStatusTransition) =>
      transition.from === investment.status &&
      transition.permissions.includes(RoleNames.ADMIN) &&
      transition.condition(investment))
    .map((transition: InvestmentStatusTransition) => ({
      status: transition.to,
      actionText: getStatusTransitionActionText(transition.to),
    }));
}

const getNumberOfActions = (filter: InvestmentFilter) : number => {
  const example = (status: any) => new InvestmentManageable({
    status,
    statusChangeLog: [{ status: InvestmentStatusValues.CONFIRMED }]
  });

  switch (filter) {
    case InvestmentSelector.ALL:
      return Object.values(InvestmentStatusValues).reduce(
        (
          result, status
        ) => Math.max(
          result,
          getStatusTransitions(example(status)).length
        ),
        0
      );
    default:
      return getStatusTransitions(example(filter)).length;
  }
}

function Row({ ...props }: any) {
  // State
  const [error, setError] = React.useState("");
  const [open, setOpen] = React.useState(false);

  const { row } = props;

  const {
    execute: updateInvestment, loading
  } = useCustomQuery(UpdateInvestment({
    onSuccess: (data: any) => {
      props.updateRow(new InvestmentManageable(data));
    },
    onError: (error: ApiErrors) => {
      switch (error) {
        case ApiErrors.INVEST_INVALID_STATUS:
          setError("Investment status invalid transition");
          break;
        default:
          break;
      }
    },
  }));

  return (
    <React.Fragment>
      {!loading ?
        <>
          <TableRow >
            <TableCell>
              <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </TableCell>

            <TableCell component="th" scope="row">
              {/* TODO(Marko): row.sale.name*/}
            </TableCell>

            <TableCell align="right">
              {row.amountShares}
            </TableCell>

            <TableCell align="right">
              {/* TODO(Marko): row.user.firstName + ' ' + row.user.lastName*/}
            </TableCell>

            <TableCell align="right">
              {/* TODO(Marko): row.user.email*/}
            </TableCell>

            <TableCell align="right" className="capital">
              {row.status}
            </TableCell>

            {getStatusTransitions(row).map((
              transition: Transition, index
            ) =>
              <TableCell align="right" key={index}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => updateInvestment({
                    variables: {
                      input: new UpdateInvestmentManageable({
                        id: row.id,
                        status: transition.status,
                      }).serialize()
                    }
                  })}
                >
                  {transition.actionText}
                </Button>
              </TableCell>)}
          </TableRow>

          <TableRow className="row">
            <TableCell className="tight" colSpan={10}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box margin={1}>
                  <Typography variant="h6" gutterBottom component="div" color="textSecondary">
                    Payment information
                  </Typography>

                  <Table size="small" aria-label="purchases">
                    <TableHead>
                      <TableRow>
                        <TableCell className="narrow bold">Payment method</TableCell>
                        <TableCell align="right" className="narrow bold">Accont holder name</TableCell>
                        <TableCell align="right" className="narrow bold">IBAN</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow key={''/* row.payment_accounts[0].bank*/}>
                        <TableCell>{/* row.payment_accounts[0].bank*/}</TableCell>
                        <TableCell align="right" >
                          {/* row.payment_accounts[0].name*/}
                        </TableCell>
                        <TableCell align="right" >{/* row.payment_accounts[0].IBAN*/}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>

          <TableRow className="row">
            <TableCell className="tight" colSpan={10}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box margin={1}>
                  <Typography variant="h6" gutterBottom component="div" color="textSecondary">
                  User basic information
                  </Typography>

                  <Table size="small" aria-label="purchases">
                    <TableHead>
                      <TableRow>
                        <TableCell className="narrow bold">
                        Address
                        </TableCell>

                        <TableCell className="narrow bold">
                        Phone number
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow key={row.id}>
                        <TableCell>
                          {/* TODO(Marko): row.user.address + ', ' + row.user.addressZipcode + ', ' + row.user.addressCity + ', ' + row.user.addressCountry*/}
                        </TableCell>

                        <TableCell>
                          {/* TODO(Marko): row.user.cellphone*/}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </>
        :
        <CircularProgress />
      }

      <CommonErrorDialog error={error} setError={setError} />
    </React.Fragment>
  );
}

export const Landing = () => {
  // State
  const [filter, setFilter] = React.useState<InvestmentFilter>(InvestmentStatusValues.AWAITING_PAYMENT);
  const [rows, setRows] = React.useState<Array<InvestmentManageable>>([]);

  // Actions
  const { execute: getInvestments } = useCustomQuery(GetInvestments({
    input: { input: new GetInvestmentManageable({ status: filter !== InvestmentSelector.ALL ? filter : undefined }).serialize() },
    onSuccess: (data: any) => {
      setRows(data ? data.map((investment: Investment) => new InvestmentManageable(investment)) : []);
    },
  }));

  // Helper
  const Filters = {
    ...InvestmentSelector,
    ...InvestmentStatusValues
  };

  return (
    <>
      <Navbar />
      <Grid container direction="row" justify="flex-start">
        <Grid item lg={1}></Grid>

        <Grid item lg={6} className="title">
          <Typography variant="h2">
          Investments
          </Typography>
        </Grid>

        <Grid item lg={1}></Grid>
      </Grid>

      <Grid container direction="row" justify="flex-end">
        <Grid item lg={1}></Grid>

        <Grid item lg={2} className="right">
          <Select
            fullWidth
            value={filter}
            labelId="demo-simple-select-label"
            id="demo-simple-select"
          >
            {Object.values(Filters).map((
              filter, index
            ) =>
              <MenuItem
                value={filter}
                onClick={() => {
                  getInvestments({ variables: { input: new GetInvestmentManageable({ status: filter !== InvestmentSelector.ALL ? filter : undefined }).serialize() } });
                  setFilter(filter);
                }}
                key={index}
              >
                {toUpperCaseFirst(filter)}
              </MenuItem>)}
          </Select>
        </Grid>

        <Grid item lg={1}></Grid>
      </Grid>

      <Grid container direction="row" justify="center">
        <Grid item lg={12}></Grid>

        <Grid item lg={10}>
          <TableContainer component={Paper}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell />

                  <TableCell className="bold">
                  Sale
                  </TableCell>

                  <TableCell align="right" className="bold">
                  Amount (€)
                  </TableCell>

                  <TableCell align="right" className="bold">
                  Name
                  </TableCell>

                  <TableCell align="right" className="bold">
                  Email
                  </TableCell>

                  <TableCell align="right" className="bold">
                  KYC status
                  </TableCell>

                  {new Array(getNumberOfActions(filter)).map((
                    _, index
                  ) =>
                    <TableCell key={index} />)}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((
                  row: InvestmentManageable, index: number
                ) =>
                  <Row
                    key={index}
                    row={row}
                    updateRow={(updatedInvestment: InvestmentManageable) => {
                      const investments = JSON.parse(JSON.stringify(rows));
                      const index = investments.findIndex((investments: InvestmentManageable) => investments.id === updatedInvestment.id);
                      investments[index] = updatedInvestment;
                      setRows(Array.from(
                        investments,
                        (investment: any) => new InvestmentManageable(investment)
                      ));
                    }}
                  />)}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
}